import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import FBRLogo from "../../Images/fbrDigitalInvoicingLogo.jpg";
import {
  getLogoUrl,
  section236HAmountFor,
  section236HFinalAmountFor,
  section236HLabel,
  SECTION_236H_FINAL_LABEL,
  hasSection236H,
} from "../../global/Utils";
import { round2 } from "../../global/money";
import { landlineForDisplay } from "../../global/landline";
import { mobileForInvoice } from "../../global/sellerPhones";
import { toWords } from "number-to-words";
import moment from "moment";

// ─── "With Company Logo" / "Logo Only" invoice patterns ─────────────────────
// A classic portrait sales-tax invoice: company letterhead on top, party boxes,
// a single items grid that runs the full height of the page, totals, amount in
// words and a signature line. Selected per company via Company.invoicePattern.
//
// The two patterns differ only in the letterhead. "With Company Logo" prints
// the logo beside typed company name / address / phone lines; "Logo Only"
// centres the uploaded artwork on its own, for companies whose logo file is
// already a full letterhead with those details baked into the image.

const BLACK = [0, 0, 0];
const GREY = [90, 90, 90];

/**
 * Money / quantity formatter with thousand separators. Two decimals - the
 * default - round the standard way (global/money.js).
 */
export const fmt = (n, dp = 2) =>
  (dp === 2 ? round2(n) : Number(n || 0)).toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });

/**
 * The tax rate is stored as the FBR rate description ("18%"), not a number, so
 * it is parsed out before formatting. Non-numeric descriptions such as "Exempt"
 * are passed through untouched rather than being coerced to 0.
 */
export const ratePct = (r) => {
  const raw = String(r ?? "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (!cleaned) return raw;
  const n = Number(cleaned);
  return Number.isFinite(n) ? `${fmt(n, 2)}%` : raw;
};

/**
 * The HS code is stamped onto the invoice line at posting time; the item record
 * is the fallback for lines saved before that field existed.
 */
export const hsCodeOf = (item) =>
  item?.hsCode || item?.itemId?.HSCodeId?.pctCode || "";

/**
 * Fetch the company logo and normalise it to a PNG data URL through a canvas.
 * The assets endpoint labels every file "image/jpg" regardless of its real
 * type, which jsPDF will not accept, so re-encoding is what makes it printable.
 * Resolves to null on any failure so a missing logo never blocks the invoice.
 */
const LOGO_TIMEOUT_MS = 8000;

export const loadLogo = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    // A stalled asset request must not block the invoice indefinitely — an
    // <img> that never loads fires neither onload nor onerror.
    const timer = setTimeout(() => finish(null), LOGO_TIMEOUT_MS);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) return finish(null);
        canvas.getContext("2d").drawImage(img, 0, 0);
        finish({
          dataUrl: canvas.toDataURL("image/png"),
          width: canvas.width,
          height: canvas.height,
        });
      } catch (_) {
        finish(null); // tainted canvas
      }
    };
    img.onerror = () => finish(null);
    img.src = url;
  });

export const generateLogoInvoice = async (data, { logoOnly = false } = {}) => {
  const doc = new jsPDF({ orientation: "p", format: "a4" });

  const PW = doc.internal.pageSize.getWidth(); // 210 mm
  const PH = doc.internal.pageSize.getHeight(); // 297 mm
  const M = 12;
  const CW = PW - M * 2; // 186 mm of content

  const company = data?.companyId || {};
  const buyer = data?.buyerId || {};
  const items = data?.items || [];
  const invoiceRef = data?.FBRInvoiceRefNo || "";

  // The FBR mark and verification QR are only meaningful once FBR has actually
  // accepted the invoice, so both are withheld until then. The top-level status
  // is what the list screen filters on; the nested response is the fallback for
  // records saved before that field was populated.
  const fbrStatus =
    data?.FBRInvoiceStatus ||
    data?.FBRInvoiceStatusResponse?.validationResponse?.status ||
    "";
  const isFbrValid =
    String(fbrStatus).toLowerCase() === "valid" && Boolean(invoiceRef);

  doc.setLineWidth(0.3);

  // ─── LETTERHEAD ──────────────────────────────────────────────────────────
  const logo = await loadLogo(getLogoUrl(company?.logo));
  let textX = M;

  // "Logo Only" prints nothing but the artwork, centred on the page: the
  // uploaded file is itself a letterhead that already carries the company name,
  // address and phone, so repeating them as text would duplicate the image. A
  // company that has not uploaded a logo falls through to the typed letterhead
  // below, so the invoice never goes out with a blank head.
  const logoOnlyHeader = logoOnly && Boolean(logo);

  if (logoOnlyHeader) {
    // Wider and taller than the beside-the-name box, since the artwork has the
    // whole head of the page to itself and has address lines to stay readable.
    const BOX_W = 120;
    const BOX_H = 28;
    const scale = Math.min(BOX_W / logo.width, BOX_H / logo.height);
    const w = logo.width * scale;
    const h = logo.height * scale;
    doc.addImage(logo.dataUrl, "PNG", (PW - w) / 2, 11 + (BOX_H - h) / 2, w, h);
  } else if (logo) {
    // Fit inside the box without distorting the artwork. The box is wide so a
    // letterhead-style horizontal logo prints at a usable size; square marks
    // stay bound by the height instead.
    const BOX_W = 46;
    const BOX_H = 22;
    const scale = Math.min(BOX_W / logo.width, BOX_H / logo.height);
    const w = logo.width * scale;
    const h = logo.height * scale;
    doc.addImage(logo.dataUrl, "PNG", M, 12 + (BOX_H - h) / 2, w, h);
    // Follow the rendered width, so a tall narrow mark leaves no dead space.
    textX = M + w + 5;
  }

  if (!logoOnlyHeader) {
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "bold");

    // The logo eats into the line, so shrink the name until it fits rather than
    // letting a long company name run off the page.
    const companyName = String(company?.companyName || "").toUpperCase();
    const nameMaxW = PW - M - textX;
    let nameSize = 19;
    doc.setFontSize(nameSize);
    while (nameSize > 10.5 && doc.getTextWidth(companyName) > nameMaxW) {
      nameSize -= 0.5;
      doc.setFontSize(nameSize);
    }
    doc.text(companyName, textX, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    if (company?.businessAddress) {
      doc.text(String(company.businessAddress), textX, 26);
    }

    // A number the company does not have is left out, label and all.
    const mobile = mobileForInvoice(company?.CEOContact);
    const landline = landlineForDisplay(company?.CEOLandline);
    const contactBits = [];
    if (mobile) contactBits.push(`Ph: ${mobile}`);
    if (landline) contactBits.push(`Landline: ${landline}`);
    if (company?.website) contactBits.push(String(company.website));
    // Wraps between the items, never inside one, so a number stays with its
    // label; a second line still sits above the title.
    const contactMaxW = PW - M - textX;
    const contactLines = contactBits.reduce((lines, bit) => {
      const joined = lines.length ? `${lines[lines.length - 1]}   ${bit}` : "";
      if (joined && doc.getTextWidth(joined) <= contactMaxW) {
        lines[lines.length - 1] = joined;
      } else {
        lines.push(bit);
      }
      return lines;
    }, []);
    contactLines.slice(0, 2).forEach((line, i) => {
      doc.text(line, textX, 31.5 + i * 4.5);
    });
  }

  doc.setTextColor(...BLACK);

  // ─── TITLE ───────────────────────────────────────────────────────────────
  const TITLE = "Sales Tax Invoice";
  doc.setFont("times", "bold");
  doc.setFontSize(16);
  const titleW = doc.getTextWidth(TITLE);
  doc.text(TITLE, PW / 2, 48, { align: "center" });
  doc.line(PW / 2 - titleW / 2, 49.6, PW / 2 + titleW / 2, 49.6);

  // ─── INVOICE META ────────────────────────────────────────────────────────
  const metaRow = (label, value, lx, ry, valueX) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(label, lx, ry);
    doc.setFont("helvetica", "normal");
    doc.text(String(value || ""), valueX, ry);
  };

  const RX = M + 108; // right meta column
  let my = 63;

  metaRow("Sales Tax Inv. :", data?.invoiceNumber, M, my, M + 30);
  metaRow(
    "Date :",
    data?.date ? moment(data.date).format("DD/MM/YYYY") : "",
    RX,
    my,
    RX + 30,
  );
  my += 6;
  metaRow("D.O/D.C No :", data?.billNo, M, my, M + 30);
  metaRow("Nature :", data?.invoiceType, RX, my, RX + 30);
  my += 6;
  metaRow("Ref/T No :", invoiceRef, M, my, M + 30);
  metaRow("Voucher No :", "", RX, my, RX + 30);
  my += 6;
  metaRow("PO# :", data?.purchaseOrderNo, M, my, M + 30);

  // ─── PARTY BOXES ─────────────────────────────────────────────────────────
  const BOX_Y = my + 4;
  const BOX_H = 32;
  const BOX_W = CW / 2;

  doc.rect(M, BOX_Y, BOX_W, BOX_H);
  doc.rect(M + BOX_W, BOX_Y, BOX_W, BOX_H);

  /** Renders one party block, clipping long values to the box width. */
  const party = (x, lines) => {
    let py = BOX_Y + 6;
    lines.forEach(([label, value, bold]) => {
      if (!label && !value) return;
      doc.setFont("times", bold ? "bold" : "normal");
      doc.setFontSize(10);
      if (label) {
        doc.text(label, x + 3, py);
        doc.setFont("times", "normal");
        doc.text(
          doc.splitTextToSize(String(value || ""), BOX_W - 40)[0] || "",
          x + 32,
          py,
        );
      } else {
        doc.text(
          doc.splitTextToSize(String(value || ""), BOX_W - 8)[0] || "",
          x + 3,
          py,
        );
      }
      py += 6;
    });
  };

  party(M, [
    ["", String(company?.companyName || "").toUpperCase(), true],
    ["", company?.businessAddress],
    ["N.T.N", company?.ntn],
    ["Sales Tax Reg. #", company?.salesTaxRegNo],
  ]);

  party(M + BOX_W, [
    ["", String(buyer?.businessName || "").toUpperCase(), true],
    ["", buyer?.address],
    ["NTN:", buyer?.registrationNo],
    ["Sales Tax Reg. #", buyer?.saletax],
  ]);

  // ─── ITEMS TABLE ─────────────────────────────────────────────────────────
  const TABLE_Y = BOX_Y + BOX_H;
  const COL_W = [45, 20, 18, 18, 24, 13, 24, 24]; // = 186 mm
  const uomLabel = items.find((it) => it?.uoM)?.uoM || "Qty";

  const head = [
    [
      { content: "Item Name", rowSpan: 2 },
      { content: "HS Code", rowSpan: 2 },
      { content: uomLabel, rowSpan: 2 },
      { content: "Rate", rowSpan: 2 },
      { content: "Value Excluding SalesTax", rowSpan: 2 },
      { content: "Sales Tax", colSpan: 2 },
      { content: "Value Inclusive SalesTax", rowSpan: 2 },
    ],
    [{ content: "%" }, { content: "Amount" }],
  ];

  const body = items.map((item) => [
    item?.itemId?.name || item?.productDescription || "",
    hsCodeOf(item),
    fmt(item?.quantity, 2),
    fmt((item?.valueSalesExcludingST || 0) / (item?.quantity || 1), 2),
    fmt(item?.valueSalesExcludingST, 2),
    ratePct(item?.rate),
    fmt(item?.salesTaxApplicable, 2),
    fmt(item?.totalValues, 2),
  ]);

  const columnStyles = {};
  COL_W.forEach((w, i) => {
    columnStyles[i] = { cellWidth: w, halign: i <= 1 ? "left" : "right" };
  });
  columnStyles[5].halign = "center";

  // Discount is deducted after the sales tax - the tax columns above are
  // charged on the undiscounted value, so only the inclusive total moves.
  // Nothing is drawn (and no room reserved) unless something was discounted.
  const totalDiscount = items.reduce(
    (acc, it) => acc + (Number(it?.discount) || 0),
    0,
  );
  // Two extra rows when discounted: the deduction and the net payable.
  const DISCOUNT_ROW_H = totalDiscount > 0 ? 14 : 0;
  // Two more, last of all: the Section 236H amount and the final amount -
  // only when the invoice carries a 236H rate.
  const show236H = hasSection236H(data?.GSection236H);
  const SECTION_236H_ROW_H = show236H ? 14 : 0;

  // Leave room below the grid for totals, amount in words and the signature.
  const TABLE_BOTTOM = PH - 62 - DISCOUNT_ROW_H - SECTION_236H_ROW_H;

  autoTable(doc, {
    startY: TABLE_Y,
    head,
    body,
    theme: "grid",
    margin: { left: M, right: M, bottom: 62 },
    styles: {
      font: "helvetica",
      fontSize: 8,
      textColor: BLACK,
      lineColor: BLACK,
      lineWidth: 0.3,
      cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1.5 },
      overflow: "ellipsize",
    },
    headStyles: {
      fillColor: false,
      textColor: BLACK,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      fontSize: 8,
      overflow: "linebreak",
    },
    bodyStyles: { fillColor: false },
    columnStyles,
  });

  // Stretch the grid down to a fixed baseline so the invoice always looks full,
  // exactly like a pre-printed stationery pad.
  let gridEnd = doc.lastAutoTable.finalY;
  if (gridEnd < TABLE_BOTTOM) {
    doc.setDrawColor(...BLACK);
    doc.rect(M, gridEnd, CW, TABLE_BOTTOM - gridEnd);
    let cx = M;
    COL_W.slice(0, -1).forEach((w) => {
      cx += w;
      doc.line(cx, gridEnd, cx, TABLE_BOTTOM);
    });
    gridEnd = TABLE_BOTTOM;
  }

  // ─── TOTALS ROW ──────────────────────────────────────────────────────────
  const sum = (fn) => items.reduce((acc, it) => acc + Number(fn(it) || 0), 0);
  const totals = {
    qty: sum((it) => it?.quantity),
    excl: data?.subTotal ?? sum((it) => it?.valueSalesExcludingST),
    tax: data?.taxTotal ?? sum((it) => it?.salesTaxApplicable),
    incl: data?.grandTotal ?? sum((it) => it?.totalValues),
  };

  const TOT_H = 7;
  doc.rect(M, gridEnd, CW, TOT_H);
  let tx = M;
  COL_W.slice(0, -1).forEach((w) => {
    tx += w;
    doc.line(tx, gridEnd, tx, gridEnd + TOT_H);
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const baseline = gridEnd + 4.8;
  const colRight = (i) =>
    M + COL_W.slice(0, i + 1).reduce((a, b) => a + b, 0) - 2;

  doc.text("Total :", colRight(0), baseline, { align: "right" });
  doc.text(fmt(totals.qty, 2), colRight(2), baseline, { align: "right" });
  doc.text(fmt(totals.excl, 2), colRight(4), baseline, { align: "right" });
  doc.text(fmt(totals.tax, 2), colRight(6), baseline, { align: "right" });
  doc.text(
    fmt(totalDiscount > 0 ? totals.incl + totalDiscount : totals.incl, 2),
    colRight(7),
    baseline,
    { align: "right" },
  );

  // The deduction and the resulting net, stacked under the totals row so the
  // discount is read against the gross figure printed directly above it. The
  // sales tax column is deliberately left untouched by both.
  let totalsBottom = gridEnd + TOT_H;
  if (totalDiscount > 0) {
    const AMT_COL_X = M + COL_W.slice(0, -1).reduce((a, b) => a + b, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    [
      ["Less : Discount", -totalDiscount],
      ["Net Payable :", totals.incl],
    ].forEach(([label, value]) => {
      const ROW_H = DISCOUNT_ROW_H / 2;
      doc.rect(M, totalsBottom, CW, ROW_H);
      doc.line(AMT_COL_X, totalsBottom, AMT_COL_X, totalsBottom + ROW_H);
      doc.text(label, colRight(0), totalsBottom + 4.8, { align: "right" });
      doc.text(fmt(value, 2), colRight(7), totalsBottom + 4.8, {
        align: "right",
      });
      totalsBottom += ROW_H;
    });
  }

  // Section 236H and the final amount, printed after the payable figure. The
  // 236H amount is never part of the invoice value - the amount in words below
  // stays the invoice total.
  if (show236H) {
    const AMT_COL_X = M + COL_W.slice(0, -1).reduce((a, b) => a + b, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    [
      [
        `${section236HLabel(data?.GSection236H)} :`,
        section236HAmountFor(totals.incl, data?.GSection236H),
      ],
      [
        `${SECTION_236H_FINAL_LABEL} :`,
        section236HFinalAmountFor(totals.incl, data?.GSection236H),
      ],
    ].forEach(([label, value]) => {
      const ROW_H = SECTION_236H_ROW_H / 2;
      doc.rect(M, totalsBottom, CW, ROW_H);
      doc.line(AMT_COL_X, totalsBottom, AMT_COL_X, totalsBottom + ROW_H);
      doc.text(label, colRight(0), totalsBottom + 4.8, { align: "right" });
      doc.text(fmt(value, 2), colRight(7), totalsBottom + 4.8, {
        align: "right",
      });
      totalsBottom += ROW_H;
    });
  }

  // ─── AMOUNT IN WORDS ─────────────────────────────────────────────────────
  let words = "";
  try {
    words = `RUPEES ${toWords(
      Math.floor(Number(totals.incl) || 0),
    ).toUpperCase()} ONLY.`;
  } catch (_) {
    words = "";
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const wordLines = doc.splitTextToSize(words, CW);
  const wordsTop = totalsBottom + 5;
  doc.text(wordLines, M, wordsTop);
  const wordsBottom = wordsTop + (wordLines.length - 1) * 4.5;

  // ─── FBR VERIFICATION (valid invoices only) ──────────────────────────────
  if (isFbrValid) {
    const MARK = 18;
    const markY = Math.max(PH - 47, wordsBottom + 4);
    const qr = await QRCode.toDataURL(invoiceRef, { width: 160, margin: 0 });

    doc.addImage(FBRLogo, "JPG", M, markY, MARK, MARK);
    doc.addImage(qr, "PNG", M + MARK + 4, markY, MARK, MARK);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...GREY);
    doc.text(`FBR Invoice # ${invoiceRef}`, M, markY + MARK + 3.5);
    doc.setTextColor(...BLACK);
  }

  // ─── SIGNATURE ───────────────────────────────────────────────────────────
  const SIG_Y = PH - 26;
  doc.line(PW - M - 55, SIG_Y, PW - M - 5, SIG_Y);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Accounts Officer", PW - M - 30, SIG_Y + 5, { align: "center" });

  // ─── PAGE NUMBERS (only when the items spill over) ────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  if (totalPages > 1) {
    for (let pg = 1; pg <= totalPages; pg++) {
      doc.setPage(pg);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...GREY);
      doc.text(`Page ${pg} of ${totalPages}`, PW - M, PH - 8, {
        align: "right",
      });
    }
  }

  const pdfBlob = doc.output("blob");
  window.open(URL.createObjectURL(pdfBlob));
};

/**
 * "Logo Only" pattern: the same invoice as above with the typed letterhead
 * dropped, leaving just the company's uploaded artwork centred at the top.
 */
export const generateLogoOnlyInvoice = (data) =>
  generateLogoInvoice(data, { logoOnly: true });
